import { useCallback, useRef, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

function isEmpty(value: unknown): boolean {
  return value == null || value === '';
}

/** Empty values stay at the bottom in both directions. */
export function compareSortValues(a: unknown, b: unknown, order: SortDirection): number {
  if (isEmpty(a) && isEmpty(b)) return 0;
  if (isEmpty(a)) return 1;
  if (isEmpty(b)) return -1;
  const direction = order === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction;
  return String(a).localeCompare(String(b), 'de', { numeric: true, sensitivity: 'base' }) * direction;
}

export function sortByColumn<T>(
  rows: readonly T[],
  valueOf: (row: T) => unknown,
  order: SortDirection
): T[] {
  return [...rows].sort((a, b) => compareSortValues(valueOf(a), valueOf(b), order));
}

export function useTableSort<K extends string>(
  initial: K,
  initialOrder: SortDirection = 'asc',
  dateColumns: readonly K[] = []
) {
  const [sort, setSort] = useState<K>(initial);
  const [order, setOrder] = useState<SortDirection>(initialOrder);
  const sortRef = useRef(sort);
  const dateColumnsRef = useRef(dateColumns);
  sortRef.current = sort;
  dateColumnsRef.current = dateColumns;

  const onSort = useCallback((column: K) => {
    if (column === sortRef.current) {
      setOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSort(column);
    setOrder(dateColumnsRef.current.includes(column) ? 'desc' : 'asc');
  }, []);

  const sortRows = <T,>(rows: readonly T[], values: Record<K, (row: T) => unknown>) =>
    sortByColumn(rows, values[sort], order);

  return { sort, order, onSort, sortRows };
}
